// Phones: focus the search input of a bottom sheet as soon as it opens.
//
// iOS only raises the keyboard when focus happens inside the user's tap, but
// a sheet's input mounts a moment later (Drawer + AutoComplete render). So on
// the tap we focus a tiny off-screen proxy input (keyboard comes up), then
// hand focus to the real input once it exists — iOS keeps the keyboard open
// when focus moves between inputs. Android/desktop simply get the focus call.

let proxyInput: HTMLInputElement | null = null;

const removeProxy = () => {
  proxyInput?.remove();
  proxyInput = null;
};

/** Call synchronously inside the tap/click handler that opens the sheet. */
export const primeMobileKeyboard = () => {
  if (typeof document === 'undefined') return;
  removeProxy();
  const input = document.createElement('input');
  input.setAttribute('type', 'text');
  input.setAttribute('aria-hidden', 'true');
  input.setAttribute('tabindex', '-1');
  input.className = 'mobile-keyboard-proxy'; // index.less
  document.body.appendChild(input);
  input.focus({ preventScroll: true });
  proxyInput = input;
  // never leave it behind if no sheet input takes over
  window.setTimeout(() => {
    if (proxyInput === input && document.activeElement === input) {
      input.blur();
    }
    if (proxyInput === input) removeProxy();
  }, 1500);
};

/**
 * Focus the first text input inside `getRoot()` once it has mounted
 * (polls briefly). Returns a cleanup that stops the polling.
 */
export const focusSheetInput = (
  getRoot: () => HTMLElement | null | undefined,
  { attempts = 20, interval = 50 } = {}
) => {
  let tries = 0;
  let timer: number | undefined;
  const tryFocus = () => {
    const input = getRoot()?.querySelector<HTMLInputElement>(
      'input:not([type="hidden"]):not([disabled])'
    );
    if (input) {
      input.focus({ preventScroll: true });
      removeProxy();
      return;
    }
    tries += 1;
    if (tries < attempts) timer = window.setTimeout(tryFocus, interval);
    else removeProxy();
  };
  timer = window.setTimeout(tryFocus, 0);
  return () => window.clearTimeout(timer);
};
